<template>
    <div class="snapshots">
        <div v-if="!loading && loadedOnce">
            <div class="columns">
                <div class="column is-half">
                    <span class="snapshots-title" style="display: inline-block; margin-bottom: 15px;">
                        <span style="font-size:14px;font-weight:600;">Backups&nbsp;&nbsp;</span>
                        <span @click="showSnapshotsFiltersModal" style="font-size: 13px;" class="tooltip cursor-pointer" data-tooltip="Filter backups"><font-awesome-icon icon="bars"/></span>
                    </span>
                </div>

                <div class="column is-half">
                    <div class="is-pulled-right dropdown is-right is-hoverable dropdown-trigger">
                        <div class="tag">
                            <span style="padding-left: 5px; padding-right: 5px;cursor:pointer;color: grey;"><font-awesome-icon icon="ellipsis-h"/></span>
                        </div>
                        <div class="dropdown-menu" id="dropdown-menu" role="menu">
                            <div class="dropdown-content">
                                <a @click="deleteAllBackups" class="dropdown-item"><font-awesome-icon icon="trash" class="dropdown-action-icon" />Delete all backups</a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
        <div class="has-text-centered" v-if="loading || !loadedOnce">
            <img src="/loader-dark.svg">
        </div>
        <table v-if="!loading && loadedOnce" class="custom-table">
            <thead>
                <th>Status</th>
                <th>File Name</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Tags</th>
                <!-- <th>Regions</th> -->
                <th>Storage</th>
                <th>Size</th>
                <th>Actions</th>
            </thead>
            <tbody>
                <tr v-if="!hasBackups">
                    <th>There are no backups to be displayed for this database. Use different filters or run your first backup now.</th>
                </tr>
                <tr v-if="!loading" v-for="backup in backups.data" v-bind:key="backup.id">
                    <th>
                        <span class="tag"
                            style="min-width:90px;"
                            :class="{ 'is-success': statusIs('completed', backup), 'is-info': statusIs('in_progress', backup), 'is-danger': statusIs('errored', backup) || statusIs('provider_deleted', backup) }"
                        >{{ statusLabel(backup) }}
                        </span>
                    </th>
                    <th>
                        <span class="column-text">
                            <span v-if="showEncryptedIcon(backup)" class="tooltip is-tooltip-multiline" :data-tooltip="backupEncryptedTooltip(backup)">
                                <font-awesome-icon icon="key"/>&nbsp;
                            </span>
                            {{ backupFileName(backup) }}
                        </span>
                    </th>
                    <th><span class="column-text tooltip" :data-tooltip="backup.created_at + ' UTC'">{{ backup.created_at_human_diff ? backup.created_at_human_diff : '-' }}</span></th>
                    <th><span class="column-text">{{ backup.duration_human_diff ? backup.duration_human_diff : '-' }}</span></th>
                    <th>
                        <span v-if="showTagsAsTags(backup)" class="tag is-light" v-for="tag in backup.tags.data" v-bind:key="tag.id">{{ tag.name }}</span>
                        <span v-if="showTagsAsCount(backup)" class="column-text tooltip" :data-tooltip="tagsList(backup)">{{ backup.tags.count }} tags</span>
                        <span v-if="backup.tags.count < 1">-</span>
                    </th>
                    <th><span class="column-text storage-link"><router-link :to="{ name: 'storageSettings', params: { id: backup.storage.id } }">{{ backup.storage.name }}</router-link></span></th>
                    <th><span class="column-text tooltip" :class="{ 'is-tooltip-multiline': backupTablesTooltipIsMultiline(backup) }" :data-tooltip="backupTablesTooltip(backup)">{{ backupSize(backup) }}</span></th>
                    <th>
                        <div :class="{ 'tooltip is-tooltip-left is-tooltip-multiline': deleteSnapshotTooltip(backup) }" :data-tooltip="deleteSnapshotTooltip(backup)">
                            <div class="dropdown is-hoverable dropdown-trigger">
                                <div class="tag">
                                    <span style="cursor:pointer;color: grey;">
                                        <font-awesome-icon icon="ellipsis-h"/>
                                    </span>
                                </div>
                                <div class="dropdown-menu" id="dropdown-menu" role="menu">
                                    <div class="dropdown-content">
                                        <!-- <a @click="askForRestoreBackupConfirmation(backup)" class="dropdown-item" :class="{ 'is-disabled-dropdown-item': backup.encrypted }">
                                            <font-awesome-icon icon="undo-alt" class="dropdown-action-icon" />Restore backup
                                        </a> -->
                                        <a @click="download(backup)" class="dropdown-item" :class="{ 'is-disabled-dropdown-item': backup.status !== 'completed' }">
                                            <font-awesome-icon icon="download" class="dropdown-action-icon" />Download backup
                                        </a>
                                        <a @click="deleteBackup(backup)" class="dropdown-item" :class="{ 'is-disabled-dropdown-item': !backupCanBeDeleted(backup) }">
                                            <font-awesome-icon icon="trash-alt" class="dropdown-action-icon" />Delete backup
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </th>
                </tr>
            </tbody>
        </table>
        <div v-if="hasBackups && !loading" class="columns is-multiline">
            <div class="column is-full p-bottom-0">
                <p class="pagination-details">Showing page <span>{{ backups.meta.current_page }}</span> of <span>{{ backups.meta.last_page }}</span></p>
            </div>
            <div class="column is-1 is-offset-5 p-top-0 has-text-right">
                <div @click="paginatePrevPage" :class="{ 'cursor-not-allowed': !prevPageAvailable}" class="button is-default-custom">
                    <font-awesome-icon icon="angle-left"/>
                </div>
            </div>
            <div class="column is-1 p-top-0">
                <div @click="paginateNextPage" :class="{ 'cursor-not-allowed': !nextPageAvailable}" class="button is-default-custom">
                    <font-awesome-icon icon="angle-right"/>
                </div>
            </div>
        </div>
        <backups-filters></backups-filters>
        <snapshot-replications></snapshot-replications>
    </div>
</template>

<style scoped>
.storage-link a {
    color: grey;
}
.storage-link a:hover {
    text-decoration: underline;
}
</style>

<script>
import Api from '@/api';
import swal from 'sweetalert';

import BackupsFilters from '@/components/Databases/Filters.vue';
import SnapshotReplications from '@/components/Cloudlets/Replications.vue';

export default {
    props: ['database'],

    components: {
        BackupsFilters,
        SnapshotReplications,
    },

    data() {
        return {
            loading: false,
            backups: [],
            page: 1,
            loadedOnce: false,
            filters: {
                tags: null,
                statuses: null,
            },
        }
    },

    mounted() {
        this.listenEvents();

        var refreshInterval = 5000;
        setInterval(() => {
            if (this.backups.data == 'undefined') {
                return;
            }

            if (this.backups.data[0] == 'undefined') {
                return;
            }

            if (this.page != 1) {
                return;
            }

            if (this.backups.data[0].status == 'pending' || this.backups.data[0].status == 'in_progress') {
                refreshInterval = 5000;
            } else {
                refreshInterval = 60000;
            }

            this.getBackups(null, false);
        }, refreshInterval);
    },

    methods: {
        getBackups(successCallback, showLoader = true) {
            if (this.loading) {
                return;
            }

            if (showLoader) {
                this.loading = true;
            }

            let endpoint = 'databases/' + this.database.id + '/backups?page=' + this.page;

            var data = {
                params: {},
            };

            data.params.tags = this.filters.tags.length > 0 ? this.filters.tags : ['not-existent-tag'];
            data.params.statuses = this.filters.statuses.length > 0 ? this.filters.statuses : ['not-existent-status'];

            Api().get(endpoint, data)
                .then((response) => {
                    this.backups = response.data;
                    if (typeof successCallback == 'function') {
                        successCallback();
                    }
                })
                .catch((error) => {
                    //
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        deleteAllBackups() {
            swal({
                title: 'Are you sure?',
                text: 'All backups will be permanently deleted. This action will run in background and can not be reversed.',
                dangerMode: true,
                buttons: {
                    cancel: 'Cancel',
                    confirm: {
                        text: 'Yes, delete all!',
                        value: true,
                        closeModal: false,
                    }
                },
                icon: 'warning',
            })
            .then((value) => {
                if (!value) {
                    swal.close();
                    return;
                }

                let endpoint = 'databases/' + this.database.id + '/backups';

                return Api().delete(endpoint)
                    .then((response) => {
                        Event.$emit('refreshBackups', () => {
                            Event.$emit('refreshDatabase', () => {
                                swal('Success!', 'All backups of this database have been scheduled for deletion. Refresh the page to check the progress.', 'success');
                            });
                        });
                    })
                    .catch((error) => {
                        swal('Oops!', 'Something happened and we could not delete the backups. Our team was notified and will check this as soon as possible.', 'error');
                    })
                    .finally(() => {
                        //
                    });
            });
        },

        deleteBackup(backup) {
            if (backup.status !== 'completed') {
                return;
            }

            swal({
                title: 'Are you sure?',
                text: 'The backup will be permanently deleted.',
                dangerMode: true,
                buttons: {
                    cancel: 'Cancel',
                    confirm: {
                        text: 'Yes, delete it!',
                        value: true,
                        closeModal: false,
                    }
                },
                icon: 'warning',
            })
            .then((value) => {
                if (!value) {
                    swal.close();
                    return;
                }

                return this.makeRequestToDeleteBackup(backup)
            });
        },

        makeRequestToDeleteBackup(backup) {
            Api().delete('backups/' + backup.id)
                .then((response) => {
                    this.getBackups(() => {
                        Event.$emit('refreshDatabase', () => {
                            swal('Success!', 'The backup was deleted.', 'success');
                        });
                    })
                })
                .catch((error) => {
                    //
                })
                .finally(() => {
                    //
                });
        },

        askForRestoreBackupConfirmation(backup) {
            if (backup.status !== 'completed') {
                return;
            }

            swal({
                title: 'Are you sure?',
                text: 'This is update the data of the target database.',
                dangerMode: true,
                buttons: {
                    cancel: 'Cancel',
                    confirm: {
                        text: 'Yes, proceed!',
                        value: true,
                        closeModal: false,
                    }
                },
                icon: 'warning',
            })
            .then((value) => {
                if (!value) {
                    swal.close();
                    return;
                }

                return this.restoreBackup(backup)
            });
        },

        restoreBackup(backup) {
            Api().post('databases/' + this.database.id + '/restores/sources/backups/' + backup.id)
                .then((response) => {
                    this.getBackups(() => {
                        Event.$emit('refreshDatabase', () => {
                            swal('Success!', 'The backup restore was initiated.', 'success');
                        });
                    })
                })
                .catch((error) => {
                    //
                })
                .finally(() => {
                    //
                });
        },

        download(backup) {
            if (backup.status !== 'completed') {
                return;
            }

            Api().get('backups/' + backup.id + '/download')
                .then((response) => {
                    window.open(response.data.url, '_blank');
                })
                .catch((error) => {
                    if (error.response.status == 422 && error.response.data.message == 'temporary_urls_not_supported') {
                        swal('Not downloadable', 'This storage does not allow the download of files via temporary URLs.', 'error');
                    }
                });
        },

        statusIs(status, snapshot) {
            return status === snapshot.status;
        },

        statusLabel(backup) {
            if (this.statusIs('expired', backup)) {
                return 'Expired';
            }
            if (this.statusIs('completed', backup)) {
                return 'Completed';
            }
            if (this.statusIs('pending', backup)) {
                return 'Pending';
            }
            if (this.statusIs('in_progress', backup)) {
                return 'In progress';
            }
            if (this.statusIs('errored', backup)) {
                return 'Failed';
            }
            if (this.statusIs('deleted', backup)) {
                return 'Manually deleted';
            }
        },

        showRegionsAsTags(snapshot) {
            // return snapshot.regions.count < 2;
        },

        showRegionsAsCount(snapshot) {
            // return snapshot.regions.count > 1;
        },

        // regionsList(snapshot) {
        //     var regions = '';
        //
        //     for (var i = 0; i < snapshot.regions.data.length; i++) {
        //         regions += snapshot.regions.data[i].name + ', ';
        //     }
        //
        //     return regions.slice(0, -2);
        // },

        showTagsAsTags(backup) {
            return backup.tags.count < 2;
        },

        showTagsAsCount(backup) {
            return backup.tags.count > 1;
        },

        tagsList(backup) {
            var tags = '';
            
            for (var i = 0; i < backup.tags.data.length; i++) {
                tags += backup.tags.data[i].name + ', ';
            }
            
            return tags.slice(0, -2);
        },

        paginatePrevPage() {
            if (!this.prevPageAvailable) {
                return;
            }

            this.page--;
            this.getBackups();
        },

        paginateNextPage() {
            if (!this.nextPageAvailable) {
                return;
            }

            this.page++;
            this.getBackups();
        },

        backupFileName(backup) {
            if (backup.file_name) {
                return backup.file_name;
            }

            return '-';
        },

        backupSize(backup) {
            if (backup.size.amount === null) {
                return '-';
            }

            return backup.size.amount + backup.size.unit;
        },

        backupCanBeDeleted(backup) {
            if (backup.in_progress) {
                return false;
            }

            if (backup.status == 'deleted') {
                return false;
            }

            if (backup.status == 'errored') {
                return false;
            }

            return true;
        },

        deleteSnapshotTooltip(snapshot) {
            // if (snapshot.in_progress) {
            //     return "Snapshot can't be deleted while it's in progress.";
            // }
            //
            // if (snapshot.status == 'errored') {
            //     return 'This snapshot failed, it will be deleted only from Weap.';
            // }
            //
            // if (snapshot.status == 'provider_deleted') {
            //     return 'This snapshot was deleted directly from ' + snapshot.provider.name + ' and now you can delete it only from Weap.'
            // }
            //
            // if (snapshot.status == 'expired') {
            //     return 'This snapshot was deleted automatically from ' + snapshot.provider.name + ' by Weap. You can also delete the Weap.io record.';
            // }
            //
            // return 'This snapshot will be deleted from Weap.io and ' + snapshot.provider.name;
        },

        showReplicationsModal(snapshot) {
            Event.$emit('showReplicationsModal', snapshot);
        },

        listenEvents() {
            var vm = this;
            Event.$on('refreshBackups', (callback) => {
                vm.getBackups(callback);
            });
            Event.$on('snapshotsFiltersUpdated', (tags, statuses) => {
                vm.filters.tags = tags;
                vm.filters.statuses = statuses;
                vm.getBackups();
            });
        },

        showSnapshotsFiltersModal() {
            Event.$emit('showSnapshotsFilters');
        },

        backupTablesTooltip(backup) {
            if (!backup.flags || backup.flags.target.type == 'all_tables') {
                return 'All tables were configured to be included in the backup.';
            }

            if (backup.flags.target.type == 'all_tables_except') {
                return 'All tables were configured to be included in the backup, except: ' + backup.flags.target.tables;
            }

            if (backup.flags.target.type == 'only_given_tables') {
                return 'Only the following tables were configured to be included in the backup: ' + backup.flags.target.tables;
            }
        },

        backupTablesTooltipIsMultiline(backup) {
            if (!backup.flags || backup.flags.target.type == 'all_tables') {
                return false;
            }

            return true;
        },

        showEncryptedIcon(backup) {
            if (backup.status == 'errored' || backup.status == 'in_progress') {
                return false;
            }

            if (!backup.encrypted) {
                return false;
            }

            return true;
        },

        backupEncryptedTooltip(backup) {
            if (!backup.encrypted) {
                return null;
            }

            return 'This backup was encrypted using the encryption key "' + backup.encryption_key.name +'"';
        },
    },

    computed: {
        hasBackups() {
            if (typeof this.backups.count === 'undefined') {
                return false;
            }

            return this.backups.count > 0;
        },

        prevPageAvailable() {
            return this.backups.meta.current_page > 1 && !this.loading;
        },

        nextPageAvailable() {
            return this.backups.meta.current_page < this.backups.meta.last_page && !this.loading;
        },
    },

    watch: {
        loading(loading) {
            this.loadedOnce = true;
        }
    },
}
</script>
